import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, interval, Subscription } from 'rxjs';
import { doc, onSnapshot } from 'firebase/firestore';
import { FirestoreService } from '../admin services/firestore.service';
import { MediaKind, PhotoTimer, Timer } from '../../interfaces/master-interfaces';




@Injectable({
  providedIn: 'root'
})



export class PhotoTimerService {

  private photoTimers = new Map<string, PhotoTimer>();
  private timers = new Map<string, Timer>();
  private timerSubscription: Subscription | null = null;
  private snapshotUnsubscribers = new Map<string, () => void>();
  private readonly EXPIRATION_TIME = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
  private photoProgressSubjects = new Map<string, BehaviorSubject<number>>();
  private docProgressSubjects = new Map<string, BehaviorSubject<number>>();

  private readonly COLLECTIONS: Record<MediaKind, string> = {
    photo: 'mapPhotos',
    video: 'mapVideos',
    audio: 'mapAudios',
  };

  constructor(
    private _firestoreService: FirestoreService
  ) {
    // Start a single interval that updates all timers
    // this.startTimerUpdates();
  }


  initTimer(docId: string, kind: MediaKind): Observable<number> {
    let subject = this.docProgressSubjects.get(docId);
    if (!subject) {
      subject = new BehaviorSubject<number>(100);
      this.docProgressSubjects.set(docId, subject);
      this.setupListener(docId, kind);
    }
    return subject.asObservable();
  }

  private setupListener(docId: string, kind: MediaKind): void {
    // Unsubscribe from previous listener if it exists
    this.snapshotUnsubscribers.get(docId)?.();

    const collectionName = this.COLLECTIONS[kind];
    if (!collectionName) {
      console.error(`[PhotoTimerService] No collection mapping for kind: ${kind}`);
      return;
    }

    const ref = doc(this._firestoreService.getFirestore(), collectionName, docId);

    const unsubscribe = onSnapshot(ref,
      snap => {
        if (snap.exists()) {
          console.log(`[PhotoTimerService] ${kind} doc data:`, snap.data());
          this.processDocData(docId, snap.data());
        } else {
          // ✅ Document no longer exists - clean up listener
          console.log(`[PhotoTimerService] Document ${collectionName}/${docId} no longer exists`);
          this.snapshotUnsubscribers.get(docId)?.();
          this.snapshotUnsubscribers.delete(docId);
        }
      },
      err => console.error(`[PhotoTimerService] listener failed for ${kind} ${docId}:`, err)
    );

    this.snapshotUnsubscribers.set(docId, unsubscribe);
  }


  private processDocData(docId: string, docData: any): void {
    if (docData && docData.timestamp) {
      let timestamp;
      if (docData.timestamp.toDate) {
        timestamp = docData.timestamp.toDate();
      } else if (docData.timestamp.seconds) {
        timestamp = new Date(docData.timestamp.seconds * 1000);
      } else {
        timestamp = new Date(docData.timestamp);
      }

      // Get the time adjustment (default to 0 if not present)
      const timeAdjustment = docData.timeAdjustment || 0; // in seconds

      // Calculate the adjusted expiration time
      // Positive timeAdjustment extends the doc's life, negative reduces it
      const expirationTime = new Date(timestamp.getTime() + this.EXPIRATION_TIME + (timeAdjustment * 1000));

      // Calculate initial percentage and store it
      const currentTime = new Date();
      const totalTimeMs = this.EXPIRATION_TIME;

      // Adjust the elapsed time calculation to account for timeAdjustment
      // For likes: doc appears older (timeAdjustment is positive, so it subtracts from remaining time)
      // For dislikes: doc appears newer (timeAdjustment is negative, so it adds to remaining time)
      const adjustedElapsedTimeMs = currentTime.getTime() - timestamp.getTime() - (timeAdjustment * 1000);
      const remainingTimeMs = Math.max(0, totalTimeMs - adjustedElapsedTimeMs);

      // Clamp percentage between 0 and 100
      const percentageRemaining = Math.max(0, Math.min(100, (remainingTimeMs / totalTimeMs) * 100));

      // Store or update the timer data
      this.timers.set(docId, {
        docId,
        expirationTime,
        percentageRemaining,
        originalTimestamp: timestamp,
        timeAdjustment
      });

      // Update the subject with the calculated percentage
      this.docProgressSubjects.get(docId)?.next(percentageRemaining);
    }
  }


  startTimerUpdates(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }

    this.timerSubscription = interval(1000).subscribe(() => {
      const currentTime = new Date();

      // Strategic console log #1: Track when each timer tick occurs and active timers count
      // console.log(`[Timer Tick] ${currentTime.toISOString()} | Photos: ${this.photoTimers.size}, Docs: ${this.timers.size}`);

      // Update each photo timer
      this.photoTimers.forEach((timer, photoId) => {
        if (currentTime >= timer.expirationTime) {
          timer.percentageRemaining = 0;
          this.photoProgressSubjects.get(photoId)?.next(0);
        } else {
          const totalTimeMs = this.EXPIRATION_TIME;
          const adjustedElapsedTimeMs = currentTime.getTime() - timer.originalTimestamp.getTime() - (timer.timeAdjustment * 1000);
          const remainingTimeMs = Math.max(0, totalTimeMs - adjustedElapsedTimeMs);
          const percentageRemaining = Math.max(0, Math.min(100, (remainingTimeMs / totalTimeMs) * 100));
          timer.percentageRemaining = percentageRemaining;
          this.photoProgressSubjects.get(photoId)?.next(percentageRemaining);
        }
      });

      // Also update the timers (docs) and docProgressSubjects
      this.timers.forEach((timer, docId) => {
        if (currentTime >= timer.expirationTime) {
          timer.percentageRemaining = 0;
          this.docProgressSubjects.get(docId)?.next(0);
        } else {
          const totalTimeMs = this.EXPIRATION_TIME;
          const adjustedElapsedTimeMs = currentTime.getTime() - timer.originalTimestamp.getTime() - (timer.timeAdjustment * 1000);
          const remainingTimeMs = Math.max(0, totalTimeMs - adjustedElapsedTimeMs);
          const percentageRemaining = Math.max(0, Math.min(100, (remainingTimeMs / totalTimeMs) * 100));
          timer.percentageRemaining = percentageRemaining;
          this.docProgressSubjects.get(docId)?.next(percentageRemaining);
        }
      });

      // Strategic console log #2: Log first photo and doc timer status for debugging
      const firstPhoto = this.photoTimers.entries().next();
      const firstDoc = this.timers.entries().next();
      if (firstPhoto.value) {
        const [photoId, photoTimer] = firstPhoto.value;
        console.log(`[Timer Status] Photo ${photoId}: ${photoTimer.percentageRemaining.toFixed(1)}% remaining}`);
      }
      if (firstDoc.value) {
        const [docId, docTimer] = firstDoc.value;
        console.log(`[Timer Status] Doc ${docId}: ${docTimer.percentageRemaining.toFixed(1)}% remaining}`);
      }
    });
  }

  /**
   * Gets the current progress for a specific photo
   */
  getPhotoProgress(photoId: string): number {
    return this.photoTimers.get(photoId)?.percentageRemaining || 100;
  }

  /**
   * Removes a photo timer when it's no longer needed
   */
  removePhotoTimer(photoId: string): void {
    // Clean up the Firestore listener
    if (this.snapshotUnsubscribers.has(photoId)) {
      this.snapshotUnsubscribers.get(photoId)?.();
      this.snapshotUnsubscribers.delete(photoId);
    }

    // Clean up the BehaviorSubject
    if (this.photoProgressSubjects.has(photoId)) {
      this.photoProgressSubjects.get(photoId)?.complete();
      this.photoProgressSubjects.delete(photoId);
    }

    // Remove from timers map
    this.photoTimers.delete(photoId);
  }

  removeTimer(docId: string): void {
    this.snapshotUnsubscribers.get(docId)?.();
    this.snapshotUnsubscribers.delete(docId);
    this.docProgressSubjects.get(docId)?.complete();
    this.docProgressSubjects.delete(docId);
    this.timers.delete(docId);
  }

  ngOnDestroy(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }

    // Clean up all Firestore listeners
    this.snapshotUnsubscribers.forEach(unsubscribe => {
      unsubscribe();
    });
    this.snapshotUnsubscribers.clear();

    // Clear all BehaviorSubjects
    this.photoProgressSubjects.forEach(subject => {
      subject.complete();
    });
    this.photoProgressSubjects.clear();
    this.photoTimers.clear();
  }
}